import React, { useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Plus, Leaf, Flame, Package, ArrowRight } from "lucide-react";
import { FLAVORS, ASSETS, FLAVOR_IMAGES, SHOP_OPEN } from "../lib/content";
import { useCart } from "../lib/cart";
import { BundleModal } from "./BundleModal";

const Card = ({ f, i, onJoin }) => {
  const { add, setOpen } = useCart();

  return (
    <motion.div
      initial={{ opacity: 0, y: 40 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-60px" }}
      transition={{ duration: 0.6, delay: (i % 3) * 0.08, ease: [0.16, 1, 0.3, 1] }}
      data-testid={`flavor-card-${f.id}`}
      className="group relative flex flex-col justify-between rounded-lg border border-white/10 bg-ash overflow-hidden hover:border-ember/60 transition-colors duration-300"
    >
      <Link to={`/flavor/${f.id}`} data-testid={`flavor-image-link-${f.id}`} className="relative overflow-hidden flex items-center justify-center bg-void" style={{ minHeight: 260 }}>
        <img
          src={FLAVOR_IMAGES[f.id] || ASSETS.packaging}
          alt={f.name}
          className="absolute inset-0 w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
        />
        <span className="absolute top-0 inset-x-0 h-2" style={{ background: f.accent }} />
        <span
          className="absolute top-5 left-4 font-mono text-[10px] uppercase tracking-widest text-void px-2.5 py-1 rounded-full"
          style={{ background: f.accent }}
        >
          {f.notes[0]}
        </span>
        <span className="absolute bottom-3 left-1/2 -translate-x-1/2 h-1.5 w-14 rounded-full" style={{ background: f.accent }} />
        <span
          data-testid={`freshness-badge-${f.id}`}
          className="absolute right-4 top-4 inline-flex items-center gap-1.5 font-mono text-[10px] uppercase tracking-widest bg-void/90 text-ember px-2.5 py-1 rounded-full"
        >
          <Flame size={11} /> Roasted this week
        </span>
        {f.featured && (
          <span className="absolute bottom-4 right-4 font-mono text-[10px] uppercase tracking-widest bg-ember text-void px-3 py-1 rounded-full">
            Founder Favorite
          </span>
        )}
      </Link>

      <div className={`p-6 flex flex-col gap-4 ${f.featured ? "" : "flex-1"}`}>
        <div>
          <Link to={`/flavor/${f.id}`} data-testid={`flavor-name-link-${f.id}`}>
            <h3 className="font-display font-extrabold uppercase tracking-tight text-cream hover:text-ember transition-colors text-2xl">
              {f.name}
            </h3>
          </Link>
          <p className="font-mono text-xs text-cream/70 mt-2 leading-relaxed max-w-sm">{f.tagline}</p>
        </div>

        <div className="flex flex-wrap gap-2">
          {f.notes.map((n) => (
            <span key={n} className="font-mono text-[10px] uppercase tracking-wider text-cream/70 border border-white/15 rounded-full px-2.5 py-1">
              {n}
            </span>
          ))}
        </div>

        <div className="mt-auto flex items-end justify-between pt-2">
          <div className="font-mono">
            <p className="text-cream text-lg font-medium">$19.99</p>
            <p className="text-ember text-[11px]">3-pack · $17.99 ea</p>
            <p className="text-cream/55 text-[10px] mt-1">12 oz whole bean</p>
          </div>
          <button
            data-testid={`flavor-add-${f.id}`}
            onClick={() => {
              if (!SHOP_OPEN) {
                toast(`${f.name} drops soon. Join the Coffee Club for first dibs.`);
                onJoin();
                return;
              }
              add(f);
              toast.success(`${f.name} added to your bag.`);
              setOpen(true);
            }}
            className="inline-flex items-center gap-2 bg-void border border-ember/40 text-ember font-mono text-[11px] uppercase tracking-widest px-4 py-2.5 rounded-full group-hover:bg-ember group-hover:text-void transition-colors duration-200"
          >
            {SHOP_OPEN ? (<><Plus size={14} /> Add</>) : "Soon"}
          </button>
        </div>
      </div>
    </motion.div>
  );
};

export const Flavors = ({ onJoin }) => {
  const [bundleOpen, setBundleOpen] = useState(false);

  return (
  <section id="shop" data-testid="flavors-section" className="relative py-24 md:py-36 px-5 md:px-10">
    <div className="max-w-[1400px] mx-auto">
      <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-14">
        <div>
          <p className="font-mono text-xs uppercase tracking-[0.3em] text-ember mb-4">The Lineup</p>
          <h2 className="font-display font-black uppercase tracking-tight text-cream text-5xl md:text-7xl leading-[0.9]">
            Pick your <span className="font-serif italic font-normal lowercase text-ember">trouble</span>
          </h2>
        </div>
        <p className="font-mono text-xs text-cream/65 flex flex-col gap-3 max-w-xs">
          <span className="flex items-center gap-2">
            <Leaf size={16} className="text-ember" /> Real ingredients, infused after the roast. All flavor, never added sweeteners. Naturally keto, gluten-free and sugar-free.
          </span>
          <Link to="/quiz" data-testid="flavors-quiz-link" className="text-ember hover:text-cream transition-colors uppercase tracking-widest text-[11px]">
            Not sure? Take the flavor quiz →
          </Link>
        </p>
      </div>

      <motion.button
        onClick={() => (SHOP_OPEN ? setBundleOpen(true) : onJoin())}
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.5 }}
        data-testid="bundle-open-button"
        className="group w-full mb-10 flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-5 border border-ember/40 bg-ash rounded-lg px-6 py-5 hover:border-ember transition-colors text-left"
      >
        <span className="w-11 h-11 rounded-full bg-ember text-void flex items-center justify-center shrink-0">
          <Package size={19} />
        </span>
        <span className="flex-1">
          <span className="block font-display font-extrabold uppercase tracking-tight text-cream text-lg group-hover:text-ember transition-colors">
            {SHOP_OPEN ? "Build your 3-pack" : "The shop opens soon"}
          </span>
          <span className="block font-mono text-xs text-cream/70 mt-1">
            {SHOP_OPEN
              ? "Mix any 3 bags and every bag drops to $17.99, saving you $6 instantly."
              : "Join the Coffee Club for first dibs, launch-day pricing and 3-pack deals ($17.99/bag)."}
          </span>
        </span>
        <span className="inline-flex items-center gap-2 font-mono text-[11px] uppercase tracking-widest text-ember">
          {SHOP_OPEN ? "Start mixing" : "Get first dibs"} <ArrowRight size={14} className="transition-transform group-hover:translate-x-1" />
        </span>
      </motion.button>

      <div className="grid grid-cols-1 md:grid-cols-3 auto-rows-auto gap-4 md:gap-5">
        {FLAVORS.map((f, i) => (
          <Card key={f.id} f={f} i={i} onJoin={onJoin} />
        ))}
      </div>
    </div>
    <BundleModal open={bundleOpen} onClose={() => setBundleOpen(false)} />
  </section>
  );
};
